import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swiper from "swiper";
import "swiper/css";

import Header from "../../../components/header";
import Footer from "../../../components/footer";
import SeoHead from "../../../components/seoHead";
import { LocalizedLink, useAppLocale } from "../../../i18n/LocaleRouting";
import { absoluteLocalizedUrl, schemaInLanguage } from "../../../i18n/seoUrls";
import { isFirebaseConfigured } from "../../../lib/firebase";
import {
  NEWS_FEED_BRAND_LABELS,
  newsFeedBrandKeyFromSlug,
  subscribeNewsItem,
  type NewsFeedItem,
} from "../../../services/newsFeedService";

import "../officialDetail.css";

function brandThemeClass(brandSlug: string): string {
  if (brandSlug === "one-piece") return "official-detail--onepiece";
  if (brandSlug === "pokemon") return "official-detail--pokemon";
  if (brandSlug === "gundam") return "official-detail--gundam";
  if (brandSlug === "magic") return "official-detail--magic";
  if (brandSlug === "dragon-ball") return "official-detail--dragon-ball";
  return "";
}

function FallbackBody({ item }: { item: NewsFeedItem }) {
  return (
    <article>
      <h1 className="text-2xl font-bold leading-snug text-[#111] mb-3">
        {item.title}
      </h1>
      {item.bodyText && item.bodyText !== item.title ? (
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-[#333] mb-4">
          {item.bodyText}
        </p>
      ) : null}
      {item.imageUrls.map((src) => (
        <img
          key={src}
          src={src}
          alt=""
          className="mb-4 w-full max-w-[560px]"
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      ))}
    </article>
  );
}

export default function OfficialNewsDetail() {
  const { t } = useTranslation();
  const locale = useAppLocale();
  const { brand = "", slug = "" } = useParams<{
    brand: string;
    slug: string;
  }>();
  const [item, setItem] = useState<NewsFeedItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!brand || !slug) {
      setItem(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = subscribeNewsItem(
      brand,
      slug,
      (next) => {
        setItem(next);
        setError(null);
        setLoading(false);
      },
      (err) => {
        setError(err.message || "Could not load this update.");
        setLoading(false);
      },
    );
    return () => unsub();
  }, [brand, slug]);

  useEffect(() => {
    const root = contentRef.current;
    if (!root || !item?.detailHtml) return;

    const instances: Swiper[] = [];

    const collectThumbItems = (thumbRoot: HTMLElement): HTMLElement[] => {
      const labeled = Array.from(
        thumbRoot.querySelectorAll<HTMLElement>(".swiper-slide"),
      );
      if (labeled.length > 0) return labeled;
      const wrapper = thumbRoot.querySelector<HTMLElement>(".swiper-wrapper");
      if (wrapper) {
        return Array.from(wrapper.children).filter(
          (node): node is HTMLElement => node instanceof HTMLElement,
        );
      }
      return Array.from(thumbRoot.querySelectorAll("img")).map((img) => {
        const host = img.closest("span, a, div.thumbnail, div");
        return (host instanceof HTMLElement ? host : img) as HTMLElement;
      });
    };

    const fallbackSrc = item.imageUrls[0] || "";
    const blockedSrc = /onepiece-cardgame\.com\/products\//i;

    const srcKey = (img: HTMLImageElement) =>
      (img.getAttribute("src") || img.src || "").split("?")[0];

    let prevSrc = "";
    Array.from(root.querySelectorAll("img")).forEach((img) => {
      const src = srcKey(img);
      if (src && src === prevSrc) {
        const wrap =
          img.closest(
            ".imgCol, .photo-panel, [data-type='component-photo'], [data-type='component-photo-gundam']",
          ) || img;
        wrap.remove();
        return;
      }
      prevSrc = src;
    });

    const slideSrc = (slide: Element): string => {
      const img = slide.querySelector("img");
      return (img?.getAttribute("src") || img?.src || "").split("?")[0];
    };

    const wrapperChildren = (host: HTMLElement): HTMLElement[] => {
      const wrapper = host.querySelector<HTMLElement>(".swiper-wrapper");
      const parent = wrapper || host;
      return Array.from(parent.children).filter(
        (node): node is HTMLElement => node instanceof HTMLElement,
      );
    };

    const makeThumbSlide = (src: string) => {
      const slide = document.createElement("span");
      slide.className = "swiper-slide";
      const img = document.createElement("img");
      img.src = src;
      img.alt = "";
      img.loading = "lazy";
      img.referrerPolicy = "no-referrer";
      slide.appendChild(img);
      return slide;
    };

    const ensureThumbs = (
      mainEl: HTMLElement,
      thumbEl: HTMLElement | null,
    ): HTMLElement => {
      let thumbs = thumbEl;
      if (!thumbs) {
        thumbs = document.createElement("div");
        thumbs.className = "sliderColThumb";
        const wrap = document.createElement("div");
        wrap.className = "swiper-wrapper";
        thumbs.appendChild(wrap);
        mainEl.insertAdjacentElement("afterend", thumbs);
      }
      thumbs.style.removeProperty("display");

      let wrapper = thumbs.querySelector<HTMLElement>(".swiper-wrapper");
      if (!wrapper) {
        wrapper = document.createElement("div");
        wrapper.className = "swiper-wrapper";
        thumbs.appendChild(wrapper);
      }

      const srcs = wrapperChildren(mainEl)
        .map(slideSrc)
        .filter((src) => Boolean(src));
      wrapper.replaceChildren(...srcs.map((src) => makeThumbSlide(src)));
      return thumbs;
    };

    const dedupeSlider = (mainEl: HTMLElement) => {
      const mainSlides = wrapperChildren(mainEl);
      const seen = new Set<string>();
      const drop: number[] = [];

      mainSlides.forEach((slide, index) => {
        const src = slideSrc(slide);
        if (!src || seen.has(src)) {
          drop.push(index);
          return;
        }
        seen.add(src);
      });

      drop
        .sort((a, b) => b - a)
        .forEach((index) => {
          mainSlides[index]?.remove();
        });
    };

    root.querySelectorAll("img").forEach((img) => {
      const applyFallback = () => {
        if (!fallbackSrc || img.src === fallbackSrc) {
          img
            .closest(".swiper-slide, .productItem, .productThumbItem")
            ?.classList.add("is-broken");
          return;
        }
        img.src = fallbackSrc;
      };

      if (blockedSrc.test(img.getAttribute("src") || "")) {
        applyFallback();
      }
      img.addEventListener("error", applyFallback);
    });

    const bindThumbs = (main: Swiper, thumbRoot: HTMLElement) => {
      const slides = collectThumbItems(thumbRoot);
      if (slides.length === 0) return;

      slides.forEach((slide) => {
        slide.classList.add("swiper-slide");
        slide.style.cursor = "pointer";
      });

      const syncActive = () => {
        slides.forEach((slide, index) => {
          slide.classList.toggle("is-thumb-active", index === main.activeIndex);
        });
      };

      const onClick = (event: Event) => {
        const target = event.target as Node | null;
        if (!target) return;
        const index = slides.findIndex((slide) => slide.contains(target));
        if (index < 0) return;
        event.preventDefault();
        event.stopPropagation();
        main.slideTo(index);
      };

      thumbRoot.addEventListener("click", onClick);
      main.on("slideChange", syncActive);
      syncActive();
    };

    root
      .querySelectorAll<HTMLElement>(".sliderCol, .productBox")
      .forEach((col) => {
        const mainEl = col.querySelector<HTMLElement>(
          ".sliderColMain.swiper, .swiper-detail, .productList, .swiper-product",
        );
        const thumbEl = col.querySelector<HTMLElement>(
          ".sliderColThumb, .swiper-thumbnail, .productThumb, .swiper-product-thumb",
        );
        if (!mainEl?.querySelector(".swiper-slide")) return;

        dedupeSlider(mainEl);

        if (!mainEl.querySelector(".swiper-slide")) return;

        const thumbs = ensureThumbs(mainEl, thumbEl);

        const main = new Swiper(mainEl, {
          slidesPerView: 1,
          spaceBetween: 0,
          observer: true,
          observeParents: true,
        });
        instances.push(main);
        bindThumbs(main, thumbs);
      });

    root
      .querySelectorAll<HTMLElement>(".slider-detail.swiper")
      .forEach((el) => {
        if (el.closest(".sliderCol")) return;
        if (!el.querySelector(".swiper-slide")) return;
        instances.push(
          new Swiper(el, {
            slidesPerView: 1,
            spaceBetween: 8,
          }),
        );
      });

    return () => {
      instances.forEach((swiper) => swiper.destroy(true, true));
    };
  }, [item?.detailHtml, item?.imageUrls]);

  const path = `/news/${brand}/${slug}`;
  const pageUrl = absoluteLocalizedUrl(path, locale);
  const newsUrl = absoluteLocalizedUrl("/news", locale);
  const backBrandKey =
    (item?.brand && newsFeedBrandKeyFromSlug(item.brand)) ||
    newsFeedBrandKeyFromSlug(brand);
  const backBrandLabel = backBrandKey
    ? t(`news.brands.${backBrandKey}`, {
        defaultValue: NEWS_FEED_BRAND_LABELS[backBrandKey] || backBrandKey,
      })
    : null;
  const backTo = backBrandKey
    ? `/news?tab=official&brand=${backBrandKey}`
    : "/news?tab=official";
  const backLabel = backBrandLabel
    ? t("news.backOfficialBrand", { brand: backBrandLabel })
    : t("news.backOfficial");
  const description = useMemo(() => {
    const text = (item?.bodyText || item?.title || "")
      .replace(/\s+/g, " ")
      .trim();
    return text.slice(0, 160);
  }, [item]);

  const shouldNoindex =
    loading || !item || Boolean(error) || !isFirebaseConfigured;

  return (
    <div className="min-h-screen bg-[#f5f5f5]">
      <SeoHead
        title={item?.title || "Official update"}
        description={description || "Official TCG product and news update."}
        path={path}
        imageUrl={item?.imageUrls[0]}
        noindex={shouldNoindex}
        structuredData={
          item && !shouldNoindex
            ? [
                {
                  "@context": "https://schema.org",
                  "@type": "NewsArticle",
                  headline: item.title,
                  description,
                  image: item.imageUrls[0],
                  datePublished: item.createdAt?.toISOString(),
                  url: pageUrl,
                  inLanguage: schemaInLanguage(locale),
                },
                {
                  "@context": "https://schema.org",
                  "@type": "BreadcrumbList",
                  itemListElement: [
                    {
                      "@type": "ListItem",
                      position: 1,
                      name: t("news.title"),
                      item: newsUrl,
                    },
                    {
                      "@type": "ListItem",
                      position: 2,
                      name: item.title,
                      item: pageUrl,
                    },
                  ],
                },
              ]
            : undefined
        }
      />
      <Header />
      <main className="w-full max-w-[1020px] mx-auto px-4 py-6 md:px-8 md:py-10">
        <p className="mb-4 text-base md:text-lg font-medium">
          <LocalizedLink
            to={backTo}
            className="text-[#888] hover:text-[#F05A2A]"
          >
            {backLabel}
          </LocalizedLink>
        </p>
        {!isFirebaseConfigured ? (
          <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Firebase is not configured, so this update cannot load yet.
          </p>
        ) : loading ? (
          <p className="text-sm text-[#888]">Loading…</p>
        ) : error ? (
          <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </p>
        ) : !item ? (
          <p className="rounded-xl border border-[#e5e5e5] bg-white px-4 py-3 text-sm text-[#666]">
            This update is not available.
          </p>
        ) : (
          <>
            <div className="overflow-hidden rounded-xl border border-[#e5e5e5] bg-white shadow-sm">
              <div className="flex flex-wrap items-center gap-2 border-b border-[#eee] px-5 py-3">
                <span className="rounded-full border border-[#d4d4d4] bg-[#f3f3f3] px-1.5 py-0.5 text-[11px] text-[#555]">
                  {NEWS_FEED_BRAND_LABELS[item.brand] || item.displayName}
                </span>
              </div>
              <div
                ref={contentRef}
                className={`official-detail ${brandThemeClass(item.brandSlug || brand)}`}
              >
                {item.detailHtml ? (
                  <div dangerouslySetInnerHTML={{ __html: item.detailHtml }} />
                ) : (
                  <div className="px-5 py-6">
                    <FallbackBody item={item} />
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <LocalizedLink
                to="/regionalstore"
                target="_top"
                className="inline-flex items-center justify-center rounded-full border border-[#F05A2A] bg-white px-5 py-2 text-sm font-medium text-[#F05A2A] transition hover:bg-[#F05A2A] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F05A2A]"
              >
                {t("product.ctaFindStores")}
              </LocalizedLink>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
